import { Skeleton } from '@mui/material';
import { Offer } from 'types/part';
import _ from 'lodash';
import {
  useGetOrgSuppliersServerSideQuery,
  useGetPublicSuppliersServerSideQuery,
} from 'store/slices/apiV1/suppliers';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { QueryFilterOperators } from 'types/api';
import SupplierLogoPile from 'ui-component/SupplierLogo/SupplierLogoPile';

const OfferSupplierLogoPile = ({ offers }: { offers: Offer[] }) => {
  const uniqueSupplierIds = _.chain(offers).map('supplier.id').uniq().value();
  const first3SupplierIds = _.chain(uniqueSupplierIds).take(3).value();

  const { data: orgSupplierData, isFetching: isFetchingOrgSuppliers } =
    useGetOrgSuppliersServerSideQuery(
      first3SupplierIds?.length !== 0
        ? {
            pageSize: 3,
            schema: [
              'id',
              'name',
              'logo',
              'website',
              'objectId',
              { publicSupplier: ['id', 'name', 'logo', 'website', 'objectId'] },
            ],
            filters: [
              {
                field: 'objectId',
                operator: QueryFilterOperators.isAnyOf,
                value: first3SupplierIds,
              },
            ],
          }
        : skipToken
    );

  const { data: publicSupplierData, isFetching: isFetchingPublicSuppliers } =
    useGetPublicSuppliersServerSideQuery(
      first3SupplierIds?.length !== 0
        ? {
            pageSize: 3,
            schema: ['id', 'name', 'logo', 'website', 'objectId'],
            filters: [
              {
                field: 'objectId',
                operator: QueryFilterOperators.isAnyOf,
                value: first3SupplierIds,
              },
            ],
          }
        : skipToken
    );

  const suppliers = _.chain(first3SupplierIds)
    .map((id) => {
      const orgSupplier = _.find(orgSupplierData?.data, { objectId: id });
      const publicSupplier = _.find(publicSupplierData?.data, { objectId: id });
      return orgSupplier ?? publicSupplier ?? undefined;
    })
    .compact()
    .take(3)
    .value();

  if (isFetchingOrgSuppliers || isFetchingPublicSuppliers) {
    return <Skeleton variant="rounded" width={50} />;
  }

  return (
    <SupplierLogoPile
      suppliers={suppliers}
      additionalCount={Math.max(uniqueSupplierIds.length - 3, 0)}
    />
  );
};

export default OfferSupplierLogoPile;
